import { useState } from 'react';
import { Ban, Search } from 'lucide-react';
import { BILLING_PLAN_LABELS, BillingPlan, MerchantStatus } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, coincide, formatCOP } from '@/lib/cn';
import { usePanoramaDeCobros, type CobroDeComercio } from '@/lib/queries';
import { CuentaComercio } from '@/components/CuentaComercio';
import { Cargando, ErrorCarga } from '@/components/ui';

/**
 * Quién le debe qué a la plataforma.
 *
 * Cada comercio lleva una cuenta corrida: acumula con cada pedido entregado y
 * se pone en cero al registrar su pago. Es la pantalla desde la que se decide
 * a quién llamar: los vencidos arriba y en rojo — si hay que buscarlos, nadie
 * los busca.
 */
export function Cobros() {
  const panorama = usePanoramaDeCobros();
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState<CobroDeComercio | null>(null);

  if (panorama.isLoading) return <Cargando texto="Cargando los cobros…" />;
  if (panorama.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(panorama.error)}
        onReintentar={() => void panorama.refetch()}
      />
    );
  }

  const todos = panorama.data ?? [];
  const filtrados = todos.filter((m) =>
    coincide(busqueda, m.name, BILLING_PLAN_LABELS[m.plan], m.suspensionReason),
  );

  // Primero lo que arde: vencidos, luego el que más debe.
  const ordenados = [...filtrados].sort((a, b) => {
    if (a.vencido !== b.vencido) return a.vencido ? -1 : 1;
    return b.deuda - a.deuda;
  });

  const enMora = todos.filter((m) => m.vencido);
  const porCobrar = todos.reduce((s, m) => s + m.pendiente, 0);
  const acumulandose = todos.reduce((s, m) => s + m.acumulado, 0);

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-3xl text-kick-dark">Cobros a comercios</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Cada comercio acumula lo que debe —su comisión o su mensualidad, más los domiciliarios que
        pida— y al registrar su pago queda en cero y arranca de nuevo.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Metrica etiqueta="Acumulándose ahora" valor={formatCOP(acumulandose)} />
        <Metrica etiqueta="De cortes sin pagar" valor={formatCOP(porCobrar)} />
        <Metrica
          etiqueta="En mora"
          valor={String(enMora.length)}
          alerta={enMora.length > 0}
          detalle={enMora.length > 0 ? 'suspendidos por falta de pago' : 'nadie vencido'}
        />
      </div>

      <div className="relative mt-6">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray"
        />
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar comercio"
          aria-label="Buscar comercio"
          className="w-full rounded-kick border border-kick-gray/30 py-2.5 pl-9 pr-3 text-sm text-kick-ink outline-none transition focus:border-kick-red"
        />
      </div>

      <div className="mt-4 space-y-2">
        {ordenados.map((m) => (
          <Fila key={m.id} comercio={m} onAbrir={() => setAbierto(m)} />
        ))}
      </div>

      <CuentaComercio comercio={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}

function Metrica({
  etiqueta,
  valor,
  detalle,
  alerta,
}: {
  etiqueta: string;
  valor: string;
  detalle?: string;
  alerta?: boolean;
}) {
  return (
    <div className={cn('rounded-kick bg-white p-4 shadow-card', alerta && 'border-l-4 border-red-500')}>
      <p className="text-xs font-semibold uppercase tracking-wide text-kick-gray">{etiqueta}</p>
      <p className={cn('mt-1 text-2xl font-bold', alerta ? 'text-red-700' : 'text-kick-dark')}>
        {valor}
      </p>
      {!!detalle && <p className="text-xs text-kick-gray">{detalle}</p>}
    </div>
  );
}

function Fila({ comercio: m, onAbrir }: { comercio: CobroDeComercio; onAbrir: () => void }) {
  const suspendido = m.status === MerchantStatus.SUSPENDED;

  return (
    <button
      onClick={onAbrir}
      className={cn(
        'block w-full rounded-kick bg-white p-4 text-left shadow-card transition hover:bg-kick-light',
        m.vencido && 'border-l-4 border-red-500',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-kick-ink">{m.name}</span>
            <span className="rounded-full bg-kick-light px-2 py-0.5 text-xs font-semibold text-kick-gray">
              {BILLING_PLAN_LABELS[m.plan]}
              {m.plan === BillingPlan.COMMISSION && ` ${m.commissionRate}%`}
              {m.plan === BillingPlan.MONTHLY && m.monthlyFee ? ` ${formatCOP(m.monthlyFee)}` : ''}
            </span>
            {suspendido && (
              <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-900">
                <Ban className="h-3 w-3" />
                Suspendido
              </span>
            )}
          </div>
          {!!m.suspensionReason && (
            <p className="mt-1 text-xs text-red-700">{m.suspensionReason}</p>
          )}
        </div>

        <div className="text-right">
          {m.deuda > 0 ? (
            <p className={cn('text-lg font-bold', m.vencido ? 'text-red-700' : 'text-kick-dark')}>
              {formatCOP(m.deuda)}
            </p>
          ) : (
            <p className="text-sm font-semibold text-emerald-700">Al día</p>
          )}
          {m.pendiente > 0 && (
            <p className="text-xs text-kick-gray">
              {formatCOP(m.pendiente)} de cortes sin pagar
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
